// Client for the self-hosted GPU platform's task API ("Option A" of the
// ev_claude_code backend): submit an image→3D job, poll it, download the GLB.
//
// Auth: a DEDICATED PLATFORM USER (Config.AI_3D_SELF_USERNAME / _PASSWORD,
// created by an admin in the platform's Settings → user management). The
// platform's SameSite cookie cannot survive a cross-origin browser client, so
// we log in via POST /api/auth/check and replay the session token it returns
// as `Authorization: Bearer <token>` — the backend accepts that since
// 2026-08-06. Re-login happens once on 401 (token expiry, ~7 days); two
// rejected logins open a 15-minute breaker so a wrong password cannot hammer
// the box (ev-world's pattern). A static key (Config.AI_3D_SELF_API_KEY +
// EV_EXTERNAL_API_KEYS on the backend) remains supported and wins when set.
// ThreeDJobManager treats any failure here as "self host down" and falls back
// to the cloud pipeline.

import Config from "../Config.jsx";
import GlbOptimizer from "./GlbOptimizer.js";

const SKILL_IDS = '["skill-3d-generate"]';

// Image→3D engines on the GPU platform (mirrors ev-world's registry). `key` is
// ours and lives on payload.gen3d.engine; `vendorId` goes over the wire in
// model_settings. The engine is ALWAYS pinned explicitly, even for the
// default — the platform's per-account default model is admin-editable over
// there and must not decide which engine runs.
//
// `labelKey` (not a vendor name) is what teachers see: the UI never names the
// model or its version, so which engine backs "Standard" can change without
// the label going stale — and without advertising our stack.
// Default flipped to TRELLIS.2 (2026-08-07): measured on the GB10 with the same
// reference image, trellis generates in 4m01s vs hunyuan's 6m50s — and queue
// wait multiplies every saved minute. Labels stay engine-agnostic on purpose.
export const ENGINES = [
  { key: "trellis-2", vendorId: "gb10/trellis2", labelKey: "GEN3D_ENGINE_STANDARD", labelFallback: "Standard", default: true },
  { key: "hunyuan3d-2.1", vendorId: "gb10/hunyuan3d", labelKey: "GEN3D_ENGINE_ALTERNATIVE", labelFallback: "Alternative", default: false },
];

export function resolveEngine(key) {
  return ENGINES.find((e) => e.key === key) || ENGINES.find((e) => e.default) || ENGINES[0];
}
// Submit is the heaviest call (multipart image); polls are cheap.
const SUBMIT_TIMEOUT_MS = 120000;
const POLL_TIMEOUT_MS = 20000;
const DOWNLOAD_TIMEOUT_MS = 300000;
const MAX_OUTPUT_BYTES = 100 * 1024 * 1024;

class SelfHost3DError extends Error {
  constructor(message, { terminal = false, httpStatus = 0 } = {}) {
    super(message);
    this.name = "SelfHost3DError";
    // terminal: retrying/waiting cannot help (auth rejected, output invalid).
    this.terminal = terminal;
    // httpStatus: lets callers react to a specific response (404 = task gone).
    this.httpStatus = httpStatus;
  }
}

let projectIdPromise = null;

// Session state for the dedicated-user login. Module-level on purpose: one
// login serves every job in the tab, exactly like ev-world's long-lived client.
let sessionToken = null;
let loginPromise = null;
let authFailures = 0;
let breakerUntil = 0;
const LOGIN_BREAKER_MS = 15 * 60 * 1000;

function baseUrl() {
  return Config.AI_3D_SELF_API_URL();
}

async function rawFetch(method, path, { body, headers, timeoutMs = POLL_TIMEOUT_MS } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(baseUrl() + path, {
      method,
      body,
      headers: headers || {},
      signal: controller.signal,
    });
  } catch (e) {
    throw new SelfHost3DError("Self-host API unreachable: " + (e && e.message ? e.message : "network error"));
  } finally {
    clearTimeout(timer);
  }
}

// POST /api/auth/check with the dedicated user; stores the returned session
// token. Single-flight so N concurrent jobs produce one login. `{ok:false}`
// is a 200, not an HTTP error — it means wrong credentials, which retrying
// can never fix, so two of those open the breaker.
function login() {
  if (Date.now() < breakerUntil) {
    return Promise.reject(new SelfHost3DError("Self-host login suspended after repeated credential rejections", { terminal: true }));
  }
  if (!loginPromise) {
    loginPromise = (async () => {
      const resp = await rawFetch("POST", "/api/auth/check", {
        body: JSON.stringify({ username: Config.AI_3D_SELF_USERNAME(), password: Config.AI_3D_SELF_PASSWORD() }),
        headers: { "Content-Type": "application/json" },
      });
      let data = null;
      try {
        data = await resp.json();
      } catch (e) { /* fall through to the !ok check */ }
      if (!resp.ok || !data || data.ok !== true) {
        authFailures += 1;
        if (authFailures >= 2) {
          breakerUntil = Date.now() + LOGIN_BREAKER_MS;
        }
        throw new SelfHost3DError("Self-host API rejected the dedicated user credentials", { terminal: true });
      }
      if (!data.token) {
        // Backend predates the token-in-response change — the cookie it set
        // cannot cross origins, so this deployment cannot serve browsers yet.
        throw new SelfHost3DError("Self-host API too old: login response carries no token (update the backend)", { terminal: true });
      }
      authFailures = 0;
      sessionToken = data.token;
      return sessionToken;
    })().finally(() => {
      loginPromise = null;
    });
  }
  return loginPromise;
}

async function authHeader() {
  const key = Config.AI_3D_SELF_API_KEY();
  if (key) {
    return { Authorization: "Bearer " + key };
  }
  if (!Config.AI_3D_SELF_USERNAME() || !Config.AI_3D_SELF_PASSWORD()) {
    throw new SelfHost3DError("Self-host credentials not configured (Config.AI_3D_SELF_USERNAME / AI_3D_SELF_PASSWORD)", { terminal: true });
  }
  if (!sessionToken) {
    await login();
  }
  return { Authorization: "Bearer " + sessionToken };
}

async function request(method, path, { body, headers, timeoutMs = POLL_TIMEOUT_MS } = {}) {
  let auth = await authHeader();
  let resp = await rawFetch(method, path, { body, headers: Object.assign({}, auth, headers || {}), timeoutMs });

  // A 401 under session auth usually means the token expired — re-login once
  // and retry (a bool, not a loop: a second 401 is a real rejection).
  if ((resp.status === 401 || resp.status === 403) && !Config.AI_3D_SELF_API_KEY() && sessionToken) {
    sessionToken = null;
    auth = await authHeader();
    resp = await rawFetch(method, path, { body, headers: Object.assign({}, auth, headers || {}), timeoutMs });
  }

  if (resp.status === 401 || resp.status === 403) {
    throw new SelfHost3DError("Self-host API rejected the credentials (HTTP " + resp.status + ")", { terminal: true });
  }
  if (!resp.ok) {
    let detail = "";
    try {
      const data = await resp.json();
      detail = data && data.detail ? ": " + String(data.detail) : "";
    } catch (e) { /* body not JSON */ }
    throw new SelfHost3DError("Self-host API HTTP " + resp.status + detail, { httpStatus: resp.status });
  }
  return resp;
}

async function requestJson(method, path, opts) {
  const resp = await request(method, path, opts);
  try {
    return await resp.json();
  } catch (e) {
    throw new SelfHost3DError("Self-host API returned a non-JSON response");
  }
}

const SelfHost3D = {
  Error: SelfHost3DError,

  /** Forget past login rejections. Called when the teacher deliberately
   * starts a NEW generation: credentials may have just been fixed on the
   * backend, and holding the 15-minute breaker against an explicit user
   * action turns a solved problem into a confusing stale failure. Automatic
   * retries inside a running pipeline never call this, so the breaker still
   * protects the box from a wrong-password hammer loop. */
  resetAuthBreaker() {
    authFailures = 0;
    breakerUntil = 0;
  },

  // URL plus some auth material (dedicated user or static key). When this is
  // false the manager goes straight to the cloud pipeline and its console
  // breadcrumb says so — better than a doomed submit.
  isConfigured() {
    if (baseUrl() === "") return false;
    if (Config.AI_3D_SELF_API_KEY()) return true;
    return Boolean(Config.AI_3D_SELF_USERNAME() && Config.AI_3D_SELF_PASSWORD());
  },

  /** Find or create the editor's project on the platform, cached per session. */
  async ensureProject() {
    if (!projectIdPromise) {
      projectIdPromise = (async () => {
        const name = Config.AI_3D_SELF_PROJECT_NAME();
        const listing = await requestJson("GET", "/api/projects");
        // Tolerate the three shapes the platform has answered with.
        const projects = Array.isArray(listing) ? listing
          : (listing && Array.isArray(listing.projects)) ? listing.projects
          : (listing && Array.isArray(listing.items)) ? listing.items : [];
        const existing = projects.find((p) => p && p.name === name);
        if (existing && existing.project_id) {
          return existing.project_id;
        }
        const created = await requestJson("POST", "/api/projects", {
          body: JSON.stringify({ name, context: "EduVenture editor image-to-3D" }),
          headers: { "Content-Type": "application/json" },
        });
        if (!created || !created.project_id) {
          throw new SelfHost3DError("Self-host API did not return a project_id");
        }
        return created.project_id;
      })().catch((e) => {
        projectIdPromise = null; // let the next job retry
        throw e;
      });
    }
    return projectIdPromise;
  },

  /**
   * Submit an image→3D task. imageBlob is the teacher's source image; prompt
   * (material/style words) steers the PBR texture bake; engine is an ENGINES
   * key (defaulted when absent/unknown). Resolves {taskId}.
   */
  async submit({ imageBlob, filename, prompt, engine }) {
    const projectId = await this.ensureProject();
    const picked = resolveEngine(engine);
    const form = new FormData();
    form.append("project_id", projectId);
    form.append("prompt", prompt);
    form.append("skill_ids", SKILL_IDS);
    // Same shape ev-world sends: the skill picks the modality, model_settings
    // pins the engine; network stays off for a pure generation task.
    form.append("model_settings", JSON.stringify({
      provider: "media",
      model: picked.vendorId,
      network_policy: "disabled",
      allow_sensitive_data_to_online_llm: false,
    }));
    form.append("files", imageBlob, filename || "photo.png");
    const data = await requestJson("POST", "/api/tasks", { body: form, timeoutMs: SUBMIT_TIMEOUT_MS });
    if (!data || !data.task_id) {
      throw new SelfHost3DError("Self-host API did not return a task_id");
    }
    return { taskId: data.task_id };
  },

  /**
   * Poll a task. Resolves the raw task object:
   * {status, progress, error, media_job:{status,progress,message}, outputs:[...]}.
   */
  async getTask(taskId) {
    return requestJson("GET", "/api/tasks/" + encodeURIComponent(taskId));
  },

  /** Cancel a task; never throws. Retries transient failures — a lost cancel
   * leaves the GPU rendering work nobody wants, which is the one waste this
   * whole flow exists to avoid. HTTP 409 means the task already finished:
   * that IS the goal state, not a failure. */
  async cancel(taskId) {
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await request("POST", "/api/tasks/" + encodeURIComponent(taskId) + "/cancel");
        return true;
      } catch (e) {
        if (e && typeof e.message === "string" && e.message.indexOf("HTTP 409") !== -1) {
          return true;
        }
        if (attempt === 3) {
          console.warn("[3D] remote cancel failed after retries", taskId, e);
          return false;
        }
        await new Promise((resolve) => setTimeout(resolve, 2000 * attempt));
      }
    }
    return false;
  },

  /**
   * Download the finished GLB. downloadUrl comes from the task's outputs and
   * must stay verbatim (it carries a required query string); only same-origin
   * /api/outputs/... paths are accepted. Resolves a Blob.
   */
  async downloadOutput(downloadUrl) {
    let path = String(downloadUrl || "");
    if (/^https?:\/\//i.test(path)) {
      const base = new URL(baseUrl());
      const url = new URL(path);
      if (url.origin !== base.origin) {
        throw new SelfHost3DError("Output URL points off the self-host API", { terminal: true });
      }
      path = url.pathname + url.search;
    }
    if (!/^\/api\/outputs\/[A-Za-z0-9._-]+\/download/.test(path)) {
      throw new SelfHost3DError("Unexpected output URL shape", { terminal: true });
    }
    const resp = await request("GET", path, { timeoutMs: DOWNLOAD_TIMEOUT_MS });
    const blob = await resp.blob();
    if (blob.size === 0) {
      throw new SelfHost3DError("Self-host API returned an empty model file");
    }
    if (blob.size > MAX_OUTPUT_BYTES) {
      throw new SelfHost3DError("Generated model exceeds the size limit", { terminal: true });
    }
    // Header-declared length vs actual bytes catches a cut-short transfer
    // here, where a retry can fix it — not after it is stored.
    try {
      GlbOptimizer.validateGlbBytes(new Uint8Array(await blob.arrayBuffer()));
    } catch (e) {
      throw new SelfHost3DError(e && e.message ? e.message : "Invalid model download");
    }
    return blob;
  },

  /** Pick the GLB entry out of a completed task's outputs. */
  findModelOutput(task) {
    const outputs = task && Array.isArray(task.outputs) ? task.outputs : [];
    return outputs.find((o) => o && (
      o.media_type === "model/gltf-binary" ||
      (typeof o.filename === "string" && o.filename.toLowerCase().endsWith(".glb"))
    )) || null;
  },
};

export default SelfHost3D;
